import React, { useState, useRef } from "react";
import {
  MenuItem,
  Select,
  Box,
  TextField,
  Button,
  Paper,
  Popper,
  Typography,
  SxProps,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import dayjs, { Dayjs } from "dayjs";
import { convertISTtoUTC } from "../utils/utils";

//Images
import settingsImage from "../../src/assets/SettingsBroken.svg";
import { colors } from "../utils/constants";

const options = [
  "Custom Range",
  "All",
  "Today",
  "Yesterday",
  "This Month",
  "Last Month",
  "This Year",
  "Last Year",
];

interface Props {
  onDateChange: (fromDate: string, toDate: string) => void;
}

const DateDropdown: React.FC<Props> = ({ onDateChange }) => {
  const [selected, setSelected] = useState("This Month");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [openPopper, setOpenPopper] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handlePredefinedDate = (value: string) => {
    const today = dayjs();
    let from = today.startOf("day");
    let to = today.endOf("day");

    switch (value) {
      case "Today":
        break;
      case "Yesterday":
        from = today.subtract(1, "day").startOf("day");
        to = today.subtract(1, "day").endOf("day");
        break;
      case "This Month":
        from = today.startOf("month").startOf("day");
        to = today.endOf("month").endOf("day");
        break;
      case "Last Month":
        from = today.subtract(1, "month").startOf("month").startOf("day");
        to = today.subtract(1, "month").endOf("month").endOf("day");
        break;
      case "This Year":
        from = today.startOf("year").startOf("day");
        to = today.endOf("year").endOf("day");
        break;
      case "Last Year":
        from = today.subtract(1, "year").startOf("year").startOf("day");
        to = today.subtract(1, "year").endOf("year").endOf("day");
        break;
      default:
        return;
    }

    // Convert IST dates to UTC and send to backend
    const fromUTC = convertISTtoUTC(from);
    const toUTC = convertISTtoUTC(to);
    onDateChange(fromUTC, toUTC);
  };

  const handleOk = () => {
    if (fromDate && toDate) {
      if (toDate.isBefore(fromDate)) {
        alert("End date must be greater than or equal to start date");
        return;
      }

      const from = fromDate.startOf("day");
      const to = toDate.endOf("day");

      // Convert IST dates to UTC and send to backend
      const fromUTC = convertISTtoUTC(from);
      const toUTC = convertISTtoUTC(to);
      onDateChange(fromUTC, toUTC);
      setOpenPopper(false);
    }
  };

  const handleCancel = () => {
    setFromDate(null);
    setToDate(null);
    setOpenPopper(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box ref={anchorRef}>
        <Select
          sx={selectSx}
          IconComponent={KeyboardArrowDownOutlinedIcon}
          value={selected}
          onChange={() => {}}
          displayEmpty
          size="small"
          renderValue={(value) => (
            <Box display="flex" alignItems="center">
              <span>{value}</span>
            </Box>
          )}
        >
          {options.map((option) => (
            <MenuItem
              key={option}
              value={option}
              style={{
                fontSize: "14px",
                fontWeight: 400,
                backgroundColor: selected === option ? "#2E3978" : undefined,
                color: selected === option ? "#ffffff" : "#000000",
                height: "35px",
              }}
              onClick={() => {
                setSelected(option);
                if (option === "Custom Range") {
                  setOpenPopper(true);
                } else {
                  setOpenPopper(false);
                  setFromDate(null);
                  setToDate(null);
                  if (option === "All") {
                    onDateChange("", "");
                  } else {
                    handlePredefinedDate(option);
                  }
                }
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Popper
        open={openPopper}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        disablePortal
      >
        <Paper sx={calenderPaperSx}>
          <Box sx={dateRangeTitleConSx}>
            <img src={settingsImage} alt="settings" />
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
                color: "#0C2646",
              }}
            >
              Select custom date range
            </Typography>
          </Box>
          <Box sx={selectDatesConSx}>
            <Box sx={dateLabelConSx}>
              <Typography sx={dateLabelSx}>
                Select start date <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={fromDate ? fromDate.format("YYYY-MM-DD") : ""}
                onChange={(e) => setFromDate(dayjs(e.target.value))}
                slotProps={{
                  htmlInput: { max: dayjs().format("YYYY-MM-DD") },
                }}
                sx={dateTextFieldSx}
                InputProps={{
                  sx: inputPropSx,
                }}
              />
            </Box>
            <Box sx={dateLabelConSx}>
              <Typography sx={dateLabelSx}>
                Select end date<span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={toDate ? toDate.format("YYYY-MM-DD") : ""}
                onChange={(e) => setToDate(dayjs(e.target.value))}
                error={!!fromDate && !!toDate && toDate.isBefore(fromDate)}
                helperText={
                  !!fromDate &&
                  !!toDate &&
                  toDate.isBefore(fromDate) &&
                  "End date should be greater than or equal to start date."
                }
                sx={dateTextFieldSx}
                InputProps={{
                  sx: inputPropSx,
                }}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button onClick={handleCancel} sx={cancelButtonSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleOk}
                sx={okButtonSx}
                disabled={!fromDate || !toDate || toDate.isBefore(fromDate)}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

export default DateDropdown;

const selectSx: SxProps = {
  backgroundColor: colors.white,
  border: `0.5px solid ${colors.indigoBlueColor}`,
  borderRadius: "8px",
  fontSize: "14px",
  width: "200px",
  height: "40px",
  minHeight: "40px",
  "& .MuiSelect-select": {
    padding: "10px",
    display: "flex",
    alignItems: "center",
  },
  "& fieldset": {
    border: "none",
  },
};

const dateTextFieldSx: SxProps = {
  my: 1,
  "& input::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "0.5px solid #ADADAD",
      borderRadius: "4px",
    },
  },
};

const calenderPaperSx: SxProps = {
  background: "#ffffff",
  width: 500,
  borderRadius: "8px",
  mt: 2,
  display: "flex",
  flexDirection: "column",
};

const dateRangeTitleConSx: SxProps = {
  background: "#F5F5F5",
  padding: "5px 16px",
  height: "45px",
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const selectDatesConSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: 2,
};

const dateLabelConSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const dateLabelSx: SxProps = {
  color: "#282828",
  fontSize: "14px",
  fontWeight: 400,
};

const cancelButtonSx: SxProps = {
  background: "#ffffff",
  borderRadius: "8px",
  border: "0.5px solid #848484",
  color: "#848484",
  textTransform: "none",
};

const okButtonSx: SxProps = {
  background: "#2E3978",
  borderRadius: "8px",
  color: "#ffffff",
  textTransform: "none",
};

const inputPropSx: SxProps = {
  "& input": {
    color: "#797979D9",
    fontSize: "14px",
    fontWeight: 400,
  },
};
